import { useState } from "react";
import { ChevronDown, HelpCircle, Send, User, Mail, MessageSquare, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import { createFaqQuestion } from "../../lib/api";

export default function Faq({ items = [], sectionContent, elementVisibility }) {
  const eyebrow = sectionContent?.eyebrow || "FAQ";
  const headline = sectionContent?.headline || "Dúvidas frequentes.";
  const description = sectionContent?.description || "Reunimos as perguntas mais comuns para acelerar a sua decisão. Se a sua dúvida não está aqui, envie diretamente.";

  const [openId, setOpenId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", question: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  function toggle(id) {
    setOpenId(openId === id ? null : id);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.question.trim()) {
      return toast.error("Preencha todos os campos");
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      return toast.error("Email inválido");
    }
    setSending(true);
    try {
      await createFaqQuestion({ ...form });
      setSent(true);
      toast.success("Pergunta enviada com sucesso!");
    } catch (err) {
      toast.error("Erro ao enviar pergunta");
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="faq" data-testid="faq-section" className="bg-fendi/30 py-28 md:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="text-center mb-16 md:mb-20">
          <p className="eyebrow text-warmgray">{eyebrow}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-softblack mt-4 md:mt-6 leading-[1.05]">
            {headline}
          </h2>
          <p className="text-warmgray text-sm sm:text-base md:text-lg leading-relaxed mt-4 md:mt-6 font-light max-w-2xl mx-auto">
            {description}
          </p>
        </Reveal>

        {elementVisibility?.faq_items !== false && (
        <div className="max-w-3xl mx-auto space-y-3">
            {items.length === 0 && (
              <p className="text-center text-warmgray text-sm">Nenhuma pergunta frequente publicada ainda.</p>
            )}

            {items.map((faq, idx) => (
              <Reveal key={faq.id} delay={idx * 80}>
                <div
                  className={`bg-white border transition-all duration-500 ${
                    openId === faq.id
                      ? "border-champagne/60 shadow-lg shadow-champagne/5"
                      : "border-fendi/20 hover:border-fendi/50 hover:shadow-md"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggle(faq.id)}
                    className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                  >
                    <span className="text-sm md:text-[15px] text-softblack font-medium pr-4 flex items-start gap-4">
                      <span className={`mt-1 w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                        openId === faq.id
                          ? "bg-champagne text-white"
                          : "bg-fendi/20 text-warmgray"
                      }`}>
                        <span className="text-[10px] font-serif">{idx + 1}</span>
                      </span>
                      {faq.question}
                    </span>
                    <ChevronDown
                      size={18}
                      className={`shrink-0 transition-all duration-500 ${
                        openId === faq.id ? "rotate-180 text-champagne" : "text-fendi/60"
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-500 ${
                      openId === faq.id ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="border-t border-fendi/10 px-5 md:px-6 pb-5 md:pb-6 pt-4">
                      <p className="text-sm md:text-base text-warmgray leading-relaxed font-light">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

          {/* Divider */}
          {elementVisibility?.faq_form !== false && (
            <>
              <div className="flex items-center gap-4 max-w-md mx-auto mt-16 md:mt-20 mb-10">
                <div className="flex-1 h-px bg-fendi/40" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-warmgray shrink-0">ou</span>
                <div className="flex-1 h-px bg-fendi/40" />
              </div>

              <Reveal className="max-w-2xl mx-auto">
                {!showForm && !sent && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowForm(true)}
                      className="inline-flex items-center gap-2.5 px-6 py-3 bg-white border border-fendi/30 text-sm text-softblack hover:border-champagne hover:text-champagne transition-all uppercase tracking-[0.2em]"
                    >
                      <HelpCircle size={14} /> Não encontrou o que procura?
                    </button>
                  </div>
                )}

                {sent && (
                  <div className="text-center p-10 bg-white border border-fendi/30">
                    <CheckCircle size={36} className="text-champagne mx-auto mb-4" />
                    <p className="text-sm text-softblack font-medium">Pergunta enviada!</p>
                    <p className="text-xs text-warmgray mt-2">Responderemos em breve no email informado.</p>
                    <button
                      type="button"
                      onClick={() => { setSent(false); setShowForm(false); setForm({ name: "", email: "", question: "" }); }}
                      className="mt-6 inline-flex items-center gap-1.5 text-xs text-champagne hover:text-softblack transition-colors uppercase tracking-[0.2em]"
                    >
                      <Send size={11} /> Enviar outra
                    </button>
                  </div>
                )}

                {showForm && !sent && (
                  <form onSubmit={handleSubmit} className="bg-white border border-fendi/30 p-8 md:p-10 space-y-6">
                    <div className="text-center mb-2">
                      <p className="text-sm text-softblack font-medium">Envie sua pergunta</p>
                      <p className="text-xs text-warmgray mt-1.5">Responderemos em breve no email informado</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <label className="block">
                        <span className="eyebrow text-[10px] text-warmgray flex items-center gap-1.5 mb-2"><User size={11} /> Nome</span>
                        <input
                          className="w-full bg-offwhite border border-fendi/30 focus:border-champagne outline-none px-4 py-3 text-sm text-softblack transition-colors"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Seu nome"
                        />
                      </label>
                      <label className="block">
                        <span className="eyebrow text-[10px] text-warmgray flex items-center gap-1.5 mb-2"><Mail size={11} /> Email</span>
                        <input
                          className="w-full bg-offwhite border border-fendi/30 focus:border-champagne outline-none px-4 py-3 text-sm text-softblack transition-colors"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="seu@email.com"
                          type="email"
                        />
                      </label>
                    </div>

                    <label className="block">
                      <span className="eyebrow text-[10px] text-warmgray flex items-center gap-1.5 mb-2"><MessageSquare size={11} /> Pergunta</span>
                      <textarea
                        className="w-full bg-offwhite border border-fendi/30 focus:border-champagne outline-none px-4 py-3 text-sm text-softblack transition-colors resize-none"
                        rows={4}
                        value={form.question}
                        onChange={(e) => setForm({ ...form, question: e.target.value })}
                        placeholder="Escreva sua pergunta..."
                      />
                    </label>

                    <button
                      type="submit"
                      disabled={sending}
                      className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-softblack text-offwhite text-xs uppercase tracking-[0.2em] hover:bg-softblack/80 transition-colors disabled:opacity-50"
                    >
                      <Send size={12} /> {sending ? "Enviando..." : "Enviar Pergunta"}
                    </button>
                  </form>
                )}
              </Reveal>
            </>
          )}
      </div>
    </section>
  );
}
